import { useState, useEffect } from "react";
import { signup, requestPhoneOtp, verifyPhoneOtp, fetchSignupConfig } from "../../api/auth.api";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import useAuthStore from "../../store/auth.store";


// Lazy-load Cashfree's V3 web SDK on demand. Loaded once per page —
// subsequent calls hit the cached promise instantly. Same pattern
// PlanCard.jsx uses for in-app Subscribe Now; duplicated here so the
// public Signup page can launch the trial-mandate flow without
// pulling in subscriptions/* component code.
function loadCashfreeSdk() {
  if (window.Cashfree) return Promise.resolve();
  if (loadCashfreeSdk._p) return loadCashfreeSdk._p;
  loadCashfreeSdk._p = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Cashfree SDK script load failed"));
    document.head.appendChild(s);
  });
  return loadCashfreeSdk._p;
}


// Trial-mandate plan picker tiers (2026-06-22, Track A). Hardcoded to
// avoid a /signup-time round-trip — the live Plan catalog rarely
// changes and the picker only shows 4 cards. Mirror of the live
// catalog returned by `Plan.objects.filter(is_active=True,
// owner_type="USER", billing_cycle="MONTHLY")`. Keep in sync with
// `subscriptions/migrations/0011_pricing_v2_tiers.py`.
const TRIAL_PLAN_TIERS = [
  { code: "B2C_BASIC",        name: "Basic",        price_inr: 2999, monthly_credits: 300,  storage_gb: 2 },
  { code: "B2C_INTERMEDIATE", name: "Intermediate", price_inr: 4999, monthly_credits: 700,  storage_gb: 5, recommended: true },
  { code: "B2C_ELITE",        name: "Elite",        price_inr: 6999, monthly_credits: 1000, storage_gb: 7 },
  { code: "B2C_ADVANCED",     name: "Advanced",     price_inr: 8999, monthly_credits: 1500, storage_gb: 10 },
];

export default function Signup() {
  const navigate = useNavigate();
  // 2026-05-30 — Player self-signup added (founder ask). Three options:
  //   "solo"          → existing B2C (scorer / fan / etc.)
  //   "organization"  → existing B2B
  //   "player"        → NEW. Creates a User + linked Player row in one
  //                     shot; the user lands on /me/player and is locked
  //                     out of scorer surfaces.
  const [accountType, setAccountType] = useState("solo");

  // Common fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  // T207: optional self-declared persona at signup. Drives default
  // plan recommendation + dashboard layout + persona analytics from
  // day one. Empty string is fine — backend accepts blank.
  const [personaSelfDeclared, setPersonaSelfDeclared] = useState("");
  // Trial-mandate (2026-06-22, Track A). B2C solo signups select a
  // target paid plan upfront; backend creates a Cashfree mandate at
  // signup time so the T+3d trial-end charge fires automatically.
  // Default = Intermediate per founder. Mirrors the live plan catalog
  // (Plan.objects.filter(is_active=True, owner_type="USER")); see
  // backend `subscriptions/migrations/0011_pricing_v2_tiers.py`.
  const [trialPlanCode, setTrialPlanCode] = useState("B2C_INTERMEDIATE");
  // 2026-06-10 — guard against double-submit. Without this, mashing
  // "Create Account" while the request is in flight fires multiple
  // POSTs; the earlier ones get cancelled mid-stream by the browser
  // when React state churns, gunicorn logs them as `400 0` (headers
  // sent, body never finished), and the SPA's error parser falls back
  // to the generic "Signup failed. Please try again." toast — masking
  // the actual server error (usually expired phone OTP token).
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Phone OTP — gated by REQUIRE_PHONE_OTP_ON_SIGNUP. When the flag is off
  // server-side, /signup-config/ returns require_phone_otp:false and the UI
  // skips the entire OTP block; the backend serializer accepts an empty token.
  // Default true is the safe assumption while the config is still loading.
  const [requirePhoneOtp, setRequirePhoneOtp] = useState(true);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpVerificationToken, setOtpVerificationToken] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpInfo, setOtpInfo] = useState("");

  useEffect(() => {
    fetchSignupConfig()
      .then((r) => setRequirePhoneOtp(!!r.data?.require_phone_otp))
      .catch(() => { /* keep the safe default (true) on failure */ });
  }, []);

  // 2026-05-30 — stopgap: when the provider templates aren't approved
  // yet, the backend echoes the OTP back in the response as `debug_otp`
  // so the founder/early users can still complete signup. Once a real
  // channel is wired this field disappears and the inline display goes
  // away automatically.
  const [debugOtp, setDebugOtp] = useState("");
  const [debugOtpNotice, setDebugOtpNotice] = useState("");

  const handleRequestOtp = async () => {
    setOtpError(""); setOtpInfo(""); setDebugOtp(""); setDebugOtpNotice("");
    if (!phone || phone.replace(/[^\d+]/g, "").length < 10) {
      setOtpError("Enter a valid mobile number with country code.");
      return;
    }
    setOtpBusy(true);
    try {
      const r = await requestPhoneOtp(phone);
      setOtpSent(true);
      // Backend tells us whether the OTP went via WhatsApp or SMS so the
      // user knows which app to check (the fallback can flip mid-rollout).
      const ch = r.data.channel;
      const via = ch === "whatsapp"
        ? "WhatsApp"
        : ch === "sms"
          ? "SMS"
          : ch === "dev_console"
            ? "the server log (dev mode)"
            : "";
      setOtpInfo(
        `OTP sent to ${r.data.phone_masked}${via ? ` via ${via}` : ""}. Valid for 5 minutes.`
      );
      if (r.data.debug_otp) {
        setDebugOtp(r.data.debug_otp);
        setDebugOtpNotice(r.data.debug_notice || "");
        // Pre-fill the OTP input so the user just clicks Verify.
        setOtp(r.data.debug_otp);
      }
    } catch (e) {
      setOtpError(e?.response?.data?.detail || e.message || "Could not send OTP.");
    } finally {
      setOtpBusy(false);
    }
  };

  const handleVerifyOtp = async () => {
    setOtpError("");
    if (!otp || otp.length !== 6) {
      setOtpError("Enter the 6-digit code.");
      return;
    }
    setOtpBusy(true);
    try {
      const r = await verifyPhoneOtp(phone, otp);
      setOtpVerificationToken(r.data.phone_verification_token);
      setOtpVerified(true);
      setOtpInfo("Phone verified ✓");
    } catch (e) {
      setOtpError(e?.response?.data?.detail || e.message || "Verification failed.");
    } finally {
      setOtpBusy(false);
    }
  };

  // Player-signup-only profile fields. The mobile number itself comes
  // from the OTP-verified `phone` field shared with the other branches
  // (2026-06-01: OTP re-enabled for Player signup, so no separate mobile
  // input — one phone, one OTP, one canonical E.164 stamped on both
  // User.phone_number and Player.mobile server-side).
  const [playerDob, setPlayerDob] = useState("");
  const [playerRole, setPlayerRole] = useState("");
  const [playerBattingHand, setPlayerBattingHand] = useState("");
  const [playerBowlingHand, setPlayerBowlingHand] = useState("");

  // Organization fields
  const [orgName, setOrgName] = useState("");
  const [orgCategory, setOrgCategory] = useState("OTHER");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [country, setCountry] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [gstin, setGstin] = useState("");

  const [error, setError] = useState(null);

  const handleAccountTypeChange = (type) => {
    setAccountType(type);
    if (type !== "organization") {
      setOrgName("");
      setOrgCategory("OTHER");
      setAddress("");
      setCity("");
      setState("");
      setPincode("");
      setCountry("");
      setContactNumber("");
      setGstin("");
    }
    if (type !== "player") {
      setPlayerDob("");
      setPlayerRole("");
      setPlayerBattingHand("");
      setPlayerBowlingHand("");
    }
    // The persona dropdown is hidden under Player branch — its value
    // is overridden to "player" server-side anyway.
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Hard idempotency guard — if a submit is in flight, swallow the
    // event. Browsers + React can deliver duplicate submits from a
    // single user click in rare cases (form enter-key + button click,
    // touch + mouse, etc.).
    if (isSubmitting) return;
    setError(null);

    // Validation. Player signup: email is OPTIONAL (mobile is the
    // primary identifier); skip the email-required check on that branch.
    if (
      !firstName || !lastName || !password || !confirmPassword
      || (accountType !== "player" && !email)
    ) {
      setError("Please fill in all required fields");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!agreeToTerms) {
      setError("Please agree to the terms and conditions");
      return;
    }

    if (requirePhoneOtp && (!otpVerified || !otpVerificationToken)) {
      setError("Please verify your phone number with the OTP first.");
      return;
    }

    if (accountType === "organization") {
      if (!orgName) {
        setError("Please provide your organization name");
        return;
      }
    }

    // Player signup uses the OTP-verified phone (shared with the other
    // branches). The OTP gate above already blocks submission until
    // `otpVerified` is true, so no extra check is needed here.

    setIsSubmitting(true);
    try {
      const signupData = {
        // Player signup may omit email — server derives the username
        // from the verified phone (player.<E164>) in that case. Scorer
        // / Org signup still uses email as username.
        username: email || undefined,
        password,
        firstName,
        lastName,
        email: email || undefined,
        phone_verification_token: otpVerificationToken,
        // T207: send the optional persona claim so the backend can
        // persist it. Empty string is accepted (legacy / skipped).
        // For Player signup we override with "player" server-side, so
        // we leave this blank here to keep the two paths independent.
        persona_self_declared:
          accountType === "player" ? "" : personaSelfDeclared,
        accountType,
        // Trial-mandate fields (sent on every solo signup; backend
        // ignores them when the gate is off or email is on the internal
        // skip list, so it's safe to always include).
        ...(accountType === "solo" && {
          target_paid_plan_code: trialPlanCode,
          mandate_return_url: `${window.location.origin}/signup/mandate-return`,
        }),
        ...(accountType === "organization" && {
          organization: {
            name: orgName,
            category: orgCategory,
            address,
            city,
            state,
            pincode,
            country,
            contactNumber,
            gstin,
          },
        }),
        ...(accountType === "player" && {
          player: {
            // mobile no longer sent — the backend derives it from the
            // OTP-verified phone_verification_token above.
            date_of_birth: playerDob || null,
            role: playerRole,
            batting_hand: playerBattingHand,
            bowling_hand: playerBowlingHand,
          },
        }),
      };

      const res = await signup(signupData);
      // Post-signup routing:
      //   Solo + mandate gate ON  → launch Cashfree mandate via V3 SDK
      //                             (Cashfree V2 Subscriptions returns a
      //                             session_token, NOT a direct URL —
      //                             match PlanCard.jsx pattern). On
      //                             return from Cashfree,
      //                             /signup/mandate-return polls the
      //                             confirm endpoint.
      //   Solo + mandate gate OFF → /check-your-email (legacy).
      //   Player                  → /me/player.
      //   Org                     → /onboarding/org wizard.
      const data = res?.data || {};
      const hasMandate = !!(data.cashfree_mandate_url || data.session_token);
      if (accountType === "player") {
        useAuthStore.getState().setAuth({
          id: data.user_id,
          username: data.username,
          email: data.email,
          account_type: data.account_type,
          account_type_v2: data.account_type_v2,
        });
        navigate("/me/player");
      } else if (accountType === "organization") {
        useAuthStore.getState().setAuth({
          id: data.user_id,
          username: data.username,
          email: data.email,
          account_type: data.account_type,
          account_type_v2: data.account_type_v2,
        });
        navigate("/onboarding/org", { state: { fresh: true, email } });
      } else if (hasMandate) {
        // Solo + mandate path. Stash sub id so the return page can
        // confirm the mandate after Cashfree redirects back. JWT is
        // already set by the signup response — initAuth picks it up
        // on return.
        try {
          sessionStorage.setItem(
            "zcd_trial_mandate",
            JSON.stringify({
              subscription_id: data.subscription_id,
              target_plan_code: data.target_plan_code,
              trial_ends_at: data.trial_ends_at,
              email,
              ts: Date.now(),
            }),
          );
        } catch { /* sessionStorage full / locked — confirm page falls
                     back to query-string sub_id */ }
        useAuthStore.getState().setAuth({
          id: data.user_id,
          username: data.username,
          email: data.email,
          account_type: data.account_type,
          account_type_v2: data.account_type_v2,
        });
        if (data.cashfree_mandate_url) {
          // Legacy / non-V2 path — direct URL.
          window.location.assign(data.cashfree_mandate_url);
          return;
        }
        // V2 path — load Cashfree V3 SDK and launch
        // subscriptionsCheckout. Matches PlanCard.jsx behaviour.
        try {
          await loadCashfreeSdk();
          const CF = window.Cashfree;
          if (!CF) throw new Error("Cashfree SDK failed to load");
          const mode = import.meta.env.PROD ? "production" : "sandbox";
          const cashfree = CF({ mode });
          const opts = {
            subsSessionId: data.session_token,
            subscriptionSessionId: data.session_token,
            paymentSessionId: data.session_token,
          };
          let result;
          if (typeof cashfree.subscriptionsCheckout === "function") {
            result = await cashfree.subscriptionsCheckout({
              ...opts, redirectTarget: "_self",
            });
          } else if (typeof cashfree.checkout === "function") {
            result = await cashfree.checkout({
              ...opts, redirectTarget: "_self",
            });
          } else {
            throw new Error("Cashfree SDK exposes no checkout method");
          }
          if (result && result.error) {
            throw new Error(result.error.message || "Cashfree rejected the session");
          }
          // On success the SDK navigates the window; control normally
          // does not return here. If it does (popup-blocked / non-
          // redirect mode), the user is on the same page — surface a
          // helpful error so they know to retry rather than landing in
          // a half-baked account.
          setError(
            "Cashfree didn't open the authorisation page. Please retry; "
            + "if it keeps failing, contact support."
          );
          setIsSubmitting(false);
          return;
        } catch (sdkErr) {
          console.error("[signup] Cashfree SDK launch failed:", sdkErr);
          setError(
            "We created your account but couldn't open the Cashfree mandate "
            + "page (" + (sdkErr.message || "SDK error") + "). Please retry; "
            + "if it keeps failing, contact support."
          );
          setIsSubmitting(false);
          return;
        }
      } else {
        navigate("/check-your-email", { state: { email } });
      }
    } catch (err) {
      // Surface the most specific error available, including field-level
      // validation errors. Throwaway-email rejection from the backend
      // anti-abuse layer comes back under data.email[0].
      const data = err?.response?.data || {};
      // Surface the most specific error available — DRF returns either
      // {"detail": "..."} or {fieldName: ["msg"]} depending on which
      // validator fired. Try every field we care about so phone-OTP /
      // canonical-email-dedup / password-complexity errors all show.
      const firstStr = (v) => {
        if (!v) return null;
        if (typeof v === "string") return v;
        if (Array.isArray(v) && v.length > 0) return String(v[0]);
        if (typeof v === "object") {
          const inner = Object.values(v)[0];
          return firstStr(inner);
        }
        return null;
      };
      const detail =
        firstStr(data.detail) ||
        firstStr(data.email) ||
        firstStr(data.username) ||
        firstStr(data.password) ||
        firstStr(data.phone) ||
        firstStr(data.phone_verification_token) ||
        firstStr(data.first_name) ||
        firstStr(data.last_name) ||
        firstStr(data.non_field_errors) ||
        firstStr(data.message);
      // 429 Too Many Requests from the SignupThrottle on the backend
      const isThrottled = err?.response?.status === 429;
      setError(
        isThrottled
          ? "Too many signup attempts from your network. Please try again in an hour."
          : detail || "Signup failed. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-bg-primary min-h-screen text-text-primary flex justify-center items-center py-8 px-4">
      <form
        onSubmit={handleSubmit}
        data-testid="signup-form"
        className="p-8 md:p-12 rounded-2xl w-full max-w-2xl bg-bg-secondary border border-bg-main shadow-2xl"
      >
        <h1 className="mb-8 text-2xl text-center font-semibold">
          Start with your account today
        </h1>

        {/* Account Type Toggle */}
        <div className="mb-8 grid grid-cols-3 gap-2 p-1 bg-bg-main rounded-lg">
          <button
            type="button"
            data-testid="signup-type-solo"
            onClick={() => handleAccountTypeChange("solo")}
            className={`py-3 px-2 rounded-md font-medium transition-all duration-200 text-sm ${
              accountType === "solo"
                ? "bg-linear-to-t from-button-primary/60 to-button-primary/10 text-text-primary shadow-lg"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            Scorer / Solo
          </button>
          <button
            type="button"
            data-testid="signup-type-player"
            onClick={() => handleAccountTypeChange("player")}
            className={`py-3 px-2 rounded-md font-medium transition-all duration-200 text-sm ${
              accountType === "player"
                ? "bg-linear-to-t from-emerald-500/60 to-emerald-500/10 text-text-primary shadow-lg"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            Player
          </button>
          <button
            type="button"
            data-testid="signup-type-org"
            onClick={() => handleAccountTypeChange("organization")}
            className={`py-3 px-2 rounded-md font-medium transition-all duration-200 text-sm ${
              accountType === "organization"
                ? "bg-linear-to-t from-button-primary/60 to-button-primary/10 text-text-primary shadow-lg"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            Organization
          </button>
        </div>
        {accountType === "player" && (
          <div className="mb-6 -mt-2 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/30 text-[12px] text-emerald-200">
            Create your <strong>player profile</strong>. Mobile number is
            required — scorers add you to their teams by searching this
            number. Cricket details below are optional and editable later.
          </div>
        )}

        {/* Common Fields */}
        <div className="space-y-4 mb-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-text-secondary mb-2">
                First Name <span className="text-red-400">*</span>
              </label>
              <input
                data-testid="signup-first-name"
                className="w-full p-3 px-4 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-2">
                Last Name <span className="text-red-400">*</span>
              </label>
              <input
                data-testid="signup-last-name"
                className="w-full p-3 px-4 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                required
              />
            </div>
          </div>

          {/* T206: Phone OTP leads the form when the server flag
              requires it. Faster on mobile / hotspot data than email
              flows (no inbox-checking detour), which matches how most
              Indian B2C operators sign up. Email moves below as the
              secondary identifier. Player branch (2026-06-01): OTP
              re-enabled. The verified phone is what scorers will use
              to find the player when adding them to a team. */}
          {requirePhoneOtp && (
          <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-lg p-4">
            <label className="block text-sm text-text-secondary mb-2">
              <span className="font-semibold text-text-primary">Mobile number</span>
              <span className="text-red-400">*</span>
              <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-300">
                {accountType === "player"
                  ? "Verified by OTP"
                  : "Recommended — fastest signup"}
              </span>
              {otpVerified && (
                <span className="ml-2 text-emerald-400 text-xs">✓ verified</span>
              )}
            </label>
            {accountType === "player" && (
              <p className="text-[11px] text-text-secondary mb-2 opacity-80">
                Scorers will add you to their team by this number, so make
                sure it's the phone you use day-to-day.
              </p>
            )}
            <div className="flex gap-2">
              <input
                type="tel"
                data-testid="signup-phone"
                className="flex-1 p-3 px-4 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors disabled:opacity-50"
                placeholder="+91 98765 43210"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={otpVerified}
                autoComplete="tel"
                required
              />
              {!otpVerified && (
                <button
                  type="button"
                  data-testid="signup-request-otp"
                  onClick={handleRequestOtp}
                  disabled={otpBusy || !phone}
                  className="px-3 sm:px-4 py-2 rounded-lg bg-button-primary text-white text-xs sm:text-sm font-medium disabled:opacity-50 whitespace-nowrap shrink-0"
                >
                  {otpBusy ? "…" : otpSent ? "Resend" : "Send OTP"}
                </button>
              )}
            </div>
            {otpSent && !otpVerified && (
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  data-testid="signup-otp"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  className="flex-1 p-3 px-4 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors font-mono tracking-widest"
                  placeholder="6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  autoComplete="one-time-code"
                />
                <button
                  type="button"
                  data-testid="signup-verify-otp"
                  onClick={handleVerifyOtp}
                  disabled={otpBusy || otp.length !== 6}
                  className="px-3 sm:px-4 py-2 rounded-lg bg-emerald-600 text-white text-xs sm:text-sm font-medium disabled:opacity-50 whitespace-nowrap shrink-0"
                >
                  {otpBusy ? "…" : "Verify"}
                </button>
              </div>
            )}
            {otpInfo && (
              <div className="mt-2 text-xs text-emerald-400">{otpInfo}</div>
            )}
            {debugOtp && (
              <div className="mt-2 p-3 rounded bg-amber-500/10 border border-amber-500/40">
                <div className="text-[11px] text-amber-200 mb-1 break-words">
                  {debugOtpNotice || "OTP delivery is being provisioned. Use the code below:"}
                </div>
                <div className="font-mono text-base sm:text-lg tracking-[0.3em] sm:tracking-[0.5em] text-amber-100 break-all">
                  {debugOtp}
                </div>
              </div>
            )}
            {otpError && (
              <div className="mt-2 text-xs text-rose-400">{otpError}</div>
            )}
          </div>
          )}

          {/* T207: optional "I am a..." persona claim. Stored on user
              for dashboard layout + plan recommendation + analytics.
              Empty value is fine. Hidden in the Player branch — persona
              is forced to "player" server-side. */}
          {accountType !== "player" && (
          <div>
            <label className="block text-sm text-text-secondary mb-2">
              I am a... <span className="text-text-secondary opacity-60">(optional)</span>
            </label>
            <select
              data-testid="signup-persona"
              value={personaSelfDeclared}
              onChange={(e) => setPersonaSelfDeclared(e.target.value)}
              className="w-full p-3 px-4 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors"
            >
              <option value="">— pick one —</option>
              <option value="scorer">Scorer (live match coder)</option>
              <option value="coach">Coach / team analyst</option>
              <option value="academy">Academy</option>
              <option value="broadcaster">Broadcaster / OTT</option>
              <option value="federation">Federation / association</option>
              <option value="umpire">Umpire</option>
              <option value="journalist">Journalist / statistician</option>
              <option value="agent">Agent / scout</option>
              <option value="sponsor">Sponsor / brand</option>
              <option value="creator">Content creator</option>
              <option value="fan">Fan</option>
              <option value="other">Other</option>
            </select>
            <p className="mt-1 text-[10px] text-text-secondary opacity-70">
              Helps us tailor the dashboard + suggest the right plan. You can change this later.
            </p>
          </div>
          )}

          {/* T206: email now sits BELOW the phone-OTP block (was above).
              Required for scorer/org signups; OPTIONAL for the Player
              branch (mobile is the primary identifier there). Indian
              users on hotspot data prefer phone OTP as primary; email
              is the secondary path used for password recovery. */}
          <div>
            <label className="block text-sm text-text-secondary mb-2">
              Email Address
              {accountType === "player" ? (
                <span className="ml-2 text-[10px] text-text-secondary opacity-70">
                  (optional — for password recovery)
                </span>
              ) : (
                <>
                  <span className="text-red-400"> *</span>
                  <span className="ml-2 text-[10px] text-text-secondary opacity-70">
                    (for verification + password recovery)
                  </span>
                </>
              )}
            </label>
            <input
              type="email"
              data-testid="signup-email"
              className="w-full p-3 px-4 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors"
              placeholder={accountType === "player" ? "john.doe@example.com (optional)" : "john.doe@example.com"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required={accountType !== "player"}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-text-secondary mb-2">
                Password <span className="text-red-400">*</span>
              </label>
              <input
                type="password"
                data-testid="signup-password"
                className="w-full p-3 px-4 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <PasswordStrengthMeter password={password} />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-2">
                Confirm Password <span className="text-red-400">*</span>
              </label>
              <input
                type="password"
                data-testid="signup-confirm-password"
                className="w-full p-3 px-4 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-1 text-[11px] text-rose-300">Passwords don't match yet.</p>
              )}
              {confirmPassword && password && password === confirmPassword && (
                <p className="mt-1 text-[11px] text-emerald-300">✓ Matches</p>
              )}
            </div>
          </div>
        </div>

        {/* Trial-mandate plan picker — Solo branch only. Backend
            requires this when TRIAL_MANDATE_ENABLED is on and the
            email isn't @sportingzenminds.com. The 30-day trial starts
            once the Cashfree mandate is authorised; at T+30d the
            selected tier's first month is auto-debited. Customer can
            cancel anytime before trial-end with no charge. */}
        {accountType === "solo" && (
          <div className="mb-6 p-5 bg-bg-main rounded-lg border border-bg-secondary">
            <h2 className="text-base font-medium text-text-primary mb-1">
              Pick your plan
              <span className="ml-2 text-[11px] uppercase tracking-wider text-emerald-300">
                30-day trial · cancel anytime
              </span>
            </h2>
            <p className="text-[12px] text-text-secondary mb-4">
              You'll authorise a Cashfree payment mandate next. No charge
              today — your first month auto-debits at the end of the
              30-day trial unless you cancel before then.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {TRIAL_PLAN_TIERS.map((p) => (
                <button
                  key={p.code}
                  type="button"
                  data-testid={`signup-plan-${p.code.toLowerCase()}`}
                  onClick={() => setTrialPlanCode(p.code)}
                  className={`text-left p-3 rounded-lg border transition-all ${
                    trialPlanCode === p.code
                      ? "border-button-primary bg-button-primary/5 ring-1 ring-button-primary"
                      : "border-bg-secondary bg-bg-secondary hover:border-button-primary/50"
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-medium text-text-primary">
                      {p.name}
                      {p.recommended && (
                        <span className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">
                          recommended
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-sm text-text-primary tabular-nums">
                      ₹{p.price_inr.toLocaleString("en-IN")}
                      <span className="text-[10px] text-text-secondary">/mo</span>
                    </div>
                  </div>
                  <div className="text-[11px] text-text-secondary mt-1">
                    {p.monthly_credits} Zencredits · {p.storage_gb} GB storage
                  </div>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-text-secondary mt-3">
              After your 30-day trial you'll be charged{" "}
              <strong className="text-text-primary">
                ₹
                {(TRIAL_PLAN_TIERS.find((t) => t.code === trialPlanCode)?.price_inr || 0)
                  .toLocaleString("en-IN")}
              </strong>{" "}
              for your first month. All prices include 18% GST.
            </p>
          </div>
        )}

        {/* Player Cricket Details — optional, only on Player branch */}
        {accountType === "player" && (
          <div className="space-y-4 mb-6 p-6 bg-bg-main rounded-lg border border-emerald-500/20">
            <h2 className="text-lg font-medium text-text-primary mb-1">
              Cricket Details
              <span className="ml-2 text-xs text-text-secondary opacity-70">(optional)</span>
            </h2>
            <p className="text-xs text-text-secondary mb-3">
              Skip any of these and finish your profile later from your dashboard.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-text-secondary mb-2">Date of birth</label>
                <input
                  type="date"
                  data-testid="signup-player-dob"
                  className="w-full p-3 px-4 rounded-lg bg-bg-secondary text-text-primary border border-bg-main focus:border-emerald-500 outline-none transition-colors"
                  value={playerDob}
                  onChange={(e) => setPlayerDob(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-2">Primary role</label>
                <select
                  data-testid="signup-player-role"
                  className="w-full p-3 px-4 rounded-lg bg-bg-secondary text-text-primary border border-bg-main focus:border-emerald-500 outline-none transition-colors"
                  value={playerRole}
                  onChange={(e) => setPlayerRole(e.target.value)}
                >
                  <option value="">— pick one —</option>
                  <option value="BATTER">Batter</option>
                  <option value="BOWLER">Bowler</option>
                  <option value="ALL_ROUNDER">All-rounder</option>
                  <option value="WICKET_KEEPER">Wicket-keeper</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-2">Batting hand</label>
                <select
                  data-testid="signup-player-batting-hand"
                  className="w-full p-3 px-4 rounded-lg bg-bg-secondary text-text-primary border border-bg-main focus:border-emerald-500 outline-none transition-colors"
                  value={playerBattingHand}
                  onChange={(e) => setPlayerBattingHand(e.target.value)}
                >
                  <option value="">— pick one —</option>
                  <option value="R">Right</option>
                  <option value="L">Left</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-2">Bowling hand</label>
                <select
                  data-testid="signup-player-bowling-hand"
                  className="w-full p-3 px-4 rounded-lg bg-bg-secondary text-text-primary border border-bg-main focus:border-emerald-500 outline-none transition-colors"
                  value={playerBowlingHand}
                  onChange={(e) => setPlayerBowlingHand(e.target.value)}
                >
                  <option value="">— pick one —</option>
                  <option value="R">Right</option>
                  <option value="L">Left</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Organization Fields - Conditionally Rendered */}
        {accountType === "organization" && (
          <div className="space-y-4 mb-6 p-6 bg-bg-main rounded-lg border border-bg-secondary">
            <h2 className="text-lg font-medium text-text-primary mb-4">
              Organization Details
            </h2>

            <div>
              <label className="block text-sm text-text-secondary mb-2">
                Organization Name <span className="text-red-400">*</span>
              </label>
              <input
                className="w-full p-3 px-4 rounded-lg bg-bg-secondary text-text-primary border border-bg-main focus:border-button-primary outline-none transition-colors"
                placeholder="Acme Corporation"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                required={accountType === "organization"}
              />
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-2">
                Organization Type <span className="text-red-400">*</span>
              </label>
              <select
                className="w-full p-3 px-4 rounded-lg bg-bg-secondary text-text-primary border border-bg-main focus:border-button-primary outline-none transition-colors"
                value={orgCategory}
                onChange={(e) => setOrgCategory(e.target.value)}
              >
                <option value="FRANCHISE">Franchise (team)</option>
                <option value="FEDERATION">Federation / Association</option>
                <option value="LEAGUE">League</option>
                <option value="MEDIA">Media / Broadcaster</option>
                <option value="OTHER">Other</option>
              </select>
              <p className="text-xs text-text-secondary mt-1">
                Helps us tailor your experience. Choose "Other" if unsure.
              </p>
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-2">
                Address
              </label>
              <input
                className="w-full p-3 px-4 rounded-lg bg-bg-secondary text-text-primary border border-bg-main focus:border-button-primary outline-none transition-colors"
                placeholder="123 Business Street (optional)"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-text-secondary mb-2">City</label>
                <input
                  className="w-full p-3 px-4 rounded-lg bg-bg-secondary text-text-primary border border-bg-main focus:border-button-primary outline-none transition-colors"
                  placeholder="Mumbai (optional)"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-2">State</label>
                <input
                  className="w-full p-3 px-4 rounded-lg bg-bg-secondary text-text-primary border border-bg-main focus:border-button-primary outline-none transition-colors"
                  placeholder="Maharashtra (optional)"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-text-secondary mb-2">Pincode</label>
                <input
                  className="w-full p-3 px-4 rounded-lg bg-bg-secondary text-text-primary border border-bg-main focus:border-button-primary outline-none transition-colors"
                  placeholder="400001 (optional)"
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-2">Country</label>
                <input
                  className="w-full p-3 px-4 rounded-lg bg-bg-secondary text-text-primary border border-bg-main focus:border-button-primary outline-none transition-colors"
                  placeholder="India (optional)"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-2">Contact Number</label>
              <input
                type="tel"
                className="w-full p-3 px-4 rounded-lg bg-bg-secondary text-text-primary border border-bg-main focus:border-button-primary outline-none transition-colors"
                placeholder="+91 98765 43210 (optional)"
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-2">
                GSTIN
              </label>
              <input
                className="w-full p-3 px-4 rounded-lg bg-bg-secondary text-text-primary border border-bg-main focus:border-button-primary outline-none transition-colors font-mono uppercase tracking-wider"
                placeholder="22AAAAA0000A1Z5 (optional)"
                maxLength={20}
                value={gstin}
                onChange={(e) => setGstin(e.target.value.toUpperCase())}
              />
              <p className="text-xs text-text-secondary mt-1">
                Needed for tax-compliant invoices. You can add it later from the
                onboarding wizard.
              </p>
            </div>
          </div>
        )}

        {/* Terms and Conditions */}
        <div className="mb-6">
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
              className="mt-1 w-4 h-4 accent-button-primary cursor-pointer"
              required
            />
            <span className="text-sm text-text-secondary group-hover:text-text-primary transition-colors">
              I agree to the{" "}
              <a href="/terms" className="text-button-primary hover:underline">
                Terms and Conditions
              </a>{" "}
              and{" "}
              <a
                href="/privacy"
                className="text-button-primary hover:underline"
              >
                Privacy Policy
              </a>
              <span className="text-red-400"> *</span>
            </span>
          </label>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <button
          type="submit"
          data-testid="signup-submit"
          disabled={isSubmitting}
          className="w-full p-3 rounded-full bg-button-primary text-text-primary font-medium hover:opacity-90 transition-opacity shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? "Creating account…" : "Create Account"}
        </button>

        <Link
          to="/login"
          className="block py-4 text-center text-text-secondary hover:text-text-primary transition-colors"
        >
          Already have an account? Click to Login
        </Link>
      </form>
    </div>
  );
}

// Inline strength meter — checks the same rules the backend enforces
// (8+ chars, 1 upper, 1 lower, 1 digit). Shows a 4-segment bar +
// per-requirement checklist so the user knows exactly what they're
// missing before submit, avoiding a server-side validation roundtrip.
function PasswordStrengthMeter({ password }) {
  const checks = [
    { id: "len",   ok: password.length >= 8,        label: "8+ characters" },
    { id: "upper", ok: /[A-Z]/.test(password),      label: "1 uppercase letter" },
    { id: "lower", ok: /[a-z]/.test(password),      label: "1 lowercase letter" },
    { id: "digit", ok: /\d/.test(password),         label: "1 number" },
  ];
  const score = checks.filter((c) => c.ok).length; // 0..4
  if (!password) return null;
  const label =
    score === 4 ? "Strong" :
    score === 3 ? "Good" :
    score === 2 ? "Weak" :
                  "Very weak";
  const color =
    score === 4 ? "bg-emerald-500" :
    score === 3 ? "bg-emerald-400" :
    score === 2 ? "bg-amber-400" :
                  "bg-rose-500";
  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-bg-secondary rounded overflow-hidden">
          <div className={`h-full ${color} transition-all`} style={{ width: `${(score / 4) * 100}%` }} />
        </div>
        <span className="text-[10px] uppercase tracking-wider text-text-secondary">{label}</span>
      </div>
      <ul className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px]">
        {checks.map((c) => (
          <li key={c.id} className={c.ok ? "text-emerald-300" : "text-text-secondary"}>
            {c.ok ? "✓" : "○"} {c.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
